import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import {Bars3Icon, ChevronDownIcon, MagnifyingGlassIcon} from '@heroicons/react/24/outline';
import {useDispatch, useSelector} from "react-redux";
import {Fragment, useEffect, useState} from "react";
import {Link} from 'react-router-dom';
import {fetchUserDetails} from "../slices/userSlice";
import {setSelectedResource, selectSelectedResource, selectTheme} from "../slices/preferencesSlice";
import {selectAuthIsAdmin, selectAuthIsForecaster, selectManagedResourcesAuth} from "../slices/authSlice";
import {scopeResourcesToRole} from "../utils/resourceScope";

import {getResourceIcon} from "../helpers/iconHelper";
import AccountMenu from './AccountMenu';
import AnimationCircle from "./AnimationCircle";
import FormInput from './FormInput';
import BottomSheet from './ui/BottomSheet';
import MarketChip from './MarketChip';
import PredicoMark from './PredicoMark';
import PillGroup from './ui/PillGroup';
import {useBreadcrumbValue} from '../contexts/BreadcrumbContext';
import {useMarketPicker} from '../contexts/MarketPickerContext';
import useMarketsOverview from '../hooks/useMarketsOverview';
import {MARKET_STATE_DOT_CLASS, MARKET_STATE_TITLE} from '../utils/marketsOverview';
import {resourceDisplayName} from '../utils/resourceName';
import ScrollFade from './ScrollFade';
import SegmentedControl from './SegmentedControl';

const STATE_FILTER_OPTIONS = [
  {value: 'all', label: 'All'},
  {value: 'active', label: 'Active'},
  {value: 'inactive', label: 'Inactive'},
];

const MARKETS_REFRESH_MS = 60 * 1000;

// Small Active/Inactive chip, sized to sit inline next to the timezone
// (same treatment as the selected resource on the selector trigger).
const ResourceStateBadge = ({inactive}) => inactive ? (
  <span className="inline-flex shrink-0 items-center px-1.5 py-px rounded text-[10px] font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
    Inactive
  </span>
) : (
  <span className="inline-flex shrink-0 items-center px-1.5 py-px rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
    Active
  </span>
);

export default function Header({setSidebarOpen}) {
  const {userDetails, resources} = useSelector(state => state.user);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [stateFilter, setStateFilter] = useState('all');
  const [filteredResources, setFilteredResources] = useState([]);
  const { open: isResourcePanelOpen, setOpen: setIsResourcePanelOpen } = useMarketPicker();
  const selectedResource = useSelector(selectSelectedResource);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const managedResources = useSelector(selectManagedResourcesAuth);
  const dispatch = useDispatch();
  const theme = useSelector(selectTheme);
  const { breadcrumbs, description } = useBreadcrumbValue();
  // Phone app bar title (forecasters only): the last breadcrumb segment.
  const pageTitle = breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].label : '';

  // Keep document.documentElement 'dark' class in sync with Redux theme
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      return;
    }
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const apply = () => {
        mq.matches ? document.documentElement.classList.add('dark') : document.documentElement.classList.remove('dark');
      };
      apply();
      mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
      return () => { mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply); };
    }
    document.documentElement.classList.remove('dark');
  }, [theme]);

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (resources) {
      // `/user/resource` is the public markets directory (all resources); scope
      // it to what this role may switch into. Market owners only see resources
      // they own/manage; superusers and forecasters keep the full list.
      const visible = scopeResourcesToRole(resources, {
        managedResources, isAdmin, isForecaster,
      });
      const filtered = visible.filter((resource) => {
        const isInactive = resource.is_active === false;
        if (stateFilter !== 'all' && (stateFilter === 'inactive') !== isInactive) return false;
        const term = searchTerm.toLowerCase();
        return resourceDisplayName(resource).toLowerCase().includes(term) || resource.name.toLowerCase().includes(term);
      });
      // Inactive resources sink to the end; stable sort keeps the original
      // order within each group.
      filtered.sort((a, b) => (a.is_active === false) - (b.is_active === false));
      setFilteredResources(filtered);
    }
  }, [resources, searchTerm, stateFilter, managedResources, isAdmin, isForecaster]);

  const handleResourceSelect = (resourceId) => {
    dispatch(setSelectedResource(resourceId));
    setIsResourcePanelOpen(false); // Close panel after selection
  };

  // Cross-market submission state (forecasters with an open session only).
  // The Header is always mounted, so it owns the base polling.
  const marketsOverview = useMarketsOverview({fetchBase: true, refreshMs: MARKETS_REFRESH_MS});
  const marketRowById = new Map((marketsOverview?.rows || []).map((r) => [r.id, r]));
  // Amber dot on the closed selector: something is still pending somewhere.
  const marketsNeedAttention = !!marketsOverview && !marketsOverview.closed && marketsOverview.dueCount > 0;

  const renderMarketStatus = (resourceId) => {
    const row = marketRowById.get(resourceId);
    if (!row) return null;
    return (
      <span
        title={MARKET_STATE_TITLE[row.state]}
        aria-label={MARKET_STATE_TITLE[row.state]}
        className="inline-flex items-center gap-1.5 text-xs tabular-nums text-gray-500 dark:text-gray-400"
      >
        <span className={`h-2 w-2 rounded-full ${MARKET_STATE_DOT_CLASS[row.state]}`} aria-hidden="true" />
        {row.ready}/{row.total} ready
      </span>
    );
  };

  return (
    <header className="sticky z-40 bg-surface dark:bg-gray-870" style={{ top: 'var(--playground-banner-height, 0px)' }}>
      {/* lg:px-16 mirrors the main-content gutter in Layout.js so the header
          aligns with the page content and clears the PerformancePaneHandle. */}
      <div className="w-full lg:max-w-7xl 2xl:max-w-[1600px] lg:mx-auto px-2 sm:px-4 lg:px-16 flex h-14 sm:h-16 items-center gap-x-1 sm:gap-x-4">
        {/* Sidebar Toggle (on smaller screens). Forecasters get the bottom
            tab bar instead (whose More button opens the More sheet), so
            their phone header shows a proper app bar: the Predico mark and
            the current page title, where the hamburger would be. */}
        {isForecaster ? (
          <div className="lg:hidden flex min-w-0 flex-1 sm:flex-none items-center gap-2.5">
            <Link to="/dashboard" aria-label="Predico dashboard" className="flex h-10 w-10 flex-none -ml-2 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
              <PredicoMark className="h-6 w-6" />
            </Link>
            <h1 className="sm:hidden min-w-0 truncate text-[17px] font-semibold text-gray-900 dark:text-gray-100">{pageTitle}</h1>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-1.5 p-1.5 sm:-m-2.5 sm:p-2.5 text-gray-700 dark:text-gray-200 lg:hidden"
          >
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true"/>
          </button>
        )}

        {/* Breadcrumbs + description stacked */}
        {breadcrumbs.length > 0 && (
          <nav className="hidden sm:flex flex-col justify-center flex-1 min-w-0" aria-label="Breadcrumb">
            <ol className="flex items-center flex-wrap gap-x-1">
              {breadcrumbs.map((segment, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <li key={index} className="flex items-center">
                    {index > 0 && (
                      <span className="mx-1.5 text-gray-400 dark:text-gray-500 select-none">›</span>
                    )}
                    {isLast ? (
                      <span className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {segment.label}
                      </span>
                    ) : (
                      <Link
                        to={segment.href}
                        className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {segment.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ol>
            {description && (
              <p className="text-xs tracking-wide text-gray-500 dark:text-gray-400 leading-tight mt-0.5 truncate">{description}</p>
            )}
          </nav>
        )}

        {/* Empty breadcrumb spacer to maintain layout when no breadcrumbs */}
        {breadcrumbs.length === 0 && <div className="hidden sm:block flex-1" />}

        {/* Right-side items */}
        <div className="ml-auto flex min-w-0 max-w-[60%] sm:max-w-none items-center gap-x-1 sm:gap-x-4">
          {/* RESOURCE SELECTOR - Mobile: Button that opens panel | Desktop: Dropdown */}

          {/* Mobile Button (< sm): app-bar chip that opens the market picker
              bottom sheet, sized so a long market name truncates in place
              instead of pushing the layout. */}
          <div className="sm:hidden flex min-w-0 justify-end">
            <MarketChip resource={selectedResource} attention={marketsNeedAttention} onClick={() => setIsResourcePanelOpen(true)} />
          </div>

          {/* Desktop Dropdown (>= sm) */}
          <Menu as="div" className="hidden sm:flex relative items-center">
            <MenuButton className="flex items-center text-gray-700 hover:text-gray-900 dark:text-gray-50 dark:hover:text-white">
              {selectedResource ? (
                <>
                  <span className="relative">
                    {getResourceIcon(selectedResource)}
                    {marketsNeedAttention && (
                      <span
                        title="Submissions pending in one or more markets"
                        className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-surface dark:ring-gray-870"
                      />
                    )}
                  </span>
                  <div className="ml-2 flex flex-col text-left">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{resourceDisplayName(selectedResource)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-300">{selectedResource.timezone || 'No timezone'}</span>
                      {selectedResource.is_active !== false ? (
                        <span className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-300">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400 dark:text-gray-300 self-center" aria-hidden="true"/>
                </>
              ) : (
                <AnimationCircle size={6}/>
              )}
            </MenuButton>

            <MenuItems className="absolute right-0 top-full mt-1 w-96 origin-top-right bg-white dark:bg-gray-870 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/20 focus:outline-none text-gray-900 dark:text-gray-100 rounded-lg overflow-hidden z-50">
              {/* Search + state quick-filter */}
              <div className="p-4 border-b border-gray-200/70 dark:border-gray-600/70 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <FormInput
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    leftIcon="search"
                    size="medium"
                  />
                </div>
                {/* Filters slide away while typing to give the input room */}
                <div className={`shrink-0 overflow-hidden transition-all duration-200 ease-out ${searchFocused ? 'max-w-0 opacity-0' : 'max-w-[240px] opacity-100'}`}>
                  <SegmentedControl
                    options={STATE_FILTER_OPTIONS}
                    value={stateFilter}
                    onChange={setStateFilter}
                    ariaLabel="Filter resources by state"
                  />
                </div>
              </div>

              {/* Filtered Resources List */}
              <ScrollFade className="max-h-80 overflow-y-auto p-2">
                {filteredResources.length > 0 ? (
                  filteredResources.map((resource, index) => {
                    const isInactive = resource.is_active === false;
                    const isSelected = selectedResource?.id === resource.id;
                    const startsInactiveGroup =
                      isInactive && index > 0 && filteredResources[index - 1].is_active !== false;
                    return (
                      <Fragment key={resource.id}>
                        {startsInactiveGroup && (
                          <div className="mx-3 my-2 border-t border-gray-200/70 dark:border-gray-600/70" />
                        )}
                        <MenuItem>
                        <div
                          className={`flex items-center gap-3 px-3 py-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                            isInactive ? 'opacity-60' : ''
                          } ${isSelected ? 'bg-brand-50 dark:bg-brand-900/30' : ''}`}
                          onClick={() => handleResourceSelect(resource.id)}
                        >
                          <div className="flex-shrink-0">{getResourceIcon(resource)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{resourceDisplayName(resource)}</div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="truncate">{resource.timezone || 'No timezone'}</span>
                              <ResourceStateBadge inactive={isInactive} />
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {renderMarketStatus(resource.id)}
                          </div>
                        </div>
                        </MenuItem>
                      </Fragment>
                    );
                  })
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No resources found
                  </div>
                )}
              </ScrollFade>
            </MenuItems>
          </Menu>

          {/* USER MENU */}
          <AccountMenu
            email={userDetails?.email}
            username={userDetails?.username}
            roleLabel={isForecaster ? 'Forecaster' : null}
          />
        </div>
      </div>

      <BottomSheet open={isResourcePanelOpen} onClose={() => setIsResourcePanelOpen(false)} title="Market">
        <label className="mt-2 flex h-12 items-center gap-2.5 rounded-xl border border-gray-300 bg-surface px-3.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500 dark:border-gray-600 dark:bg-gray-870">
          <MagnifyingGlassIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search markets"
            aria-label="Search markets"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-gray-900 placeholder-gray-400 focus:ring-0 dark:text-gray-100 dark:focus:!shadow-none"
          />
        </label>
        <div className="mt-3">
          <PillGroup options={STATE_FILTER_OPTIONS} value={stateFilter} onChange={setStateFilter} ariaLabel="Filter markets by state" />
        </div>
        <ul className="mt-3 space-y-2" aria-label="Markets">
          {filteredResources.map((resource, index) => {
            const isInactive = resource.is_active === false;
            const isSelected = selectedResource?.id === resource.id;
            const startsInactiveGroup = isInactive && index > 0 && filteredResources[index - 1].is_active !== false;
            return (
              <li key={resource.id}>
                {startsInactiveGroup && <div className="mx-3 mb-2 border-t border-gray-200/70 dark:border-gray-600/70" />}
                <button
                  type="button"
                  onClick={() => handleResourceSelect(resource.id)}
                  aria-pressed={isSelected}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                    isSelected ? 'border-brand-400 bg-brand-50 dark:border-brand-600 dark:bg-brand-900/30' : 'border-gray-200/70 dark:border-gray-600/70'
                  } ${isInactive ? 'opacity-60' : ''}`}
                >
                  <span className="flex-none">{getResourceIcon(resource)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium text-gray-900 dark:text-gray-100">{resourceDisplayName(resource)}</span>
                    <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="truncate">{resource.timezone || 'No timezone'}</span>
                      <ResourceStateBadge inactive={isInactive} />
                    </span>
                  </span>
                  <span className="flex-none">{renderMarketStatus(resource.id)}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {filteredResources.length === 0 && <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No markets found</p>}
      </BottomSheet>
    </header>
  );
}
